import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenantId } from '../../config/tenantConfig';
import apiCache from '../../utils/apiCache';
import FallbackImg from '../FallbackImg';
import './index.css';

const AshaCategories = ({ config, navigate }) => {
  const { menu } = config;
  if (!menu?.categories?.length) return null;

  return (
    <section id="categories" className="asha-categories">
      <div className="container">
        <div className="asha-categories__header text-center">
          <span className="asha-categories__kicker">{menu.sub_heading}</span>
          <h2 className="asha-categories__title">{menu.heading || 'Taste Categories'}</h2>
          <p className="asha-categories__desc">{menu.description}</p>
        </div>

        <div className="asha-categories__grid">
          {menu.categories.map((cat, idx) => (
            <div
              key={cat.id || idx}
              className="asha-categories__item"
              onClick={() => { if (cat.id) sessionStorage.setItem('pending_category_id', String(cat.id)); navigate('/restaurants'); }}
            >
              <div className="asha-categories__img-box">
                <img src={cat.image} alt={cat.name} />
                <div className="asha-categories__overlay">
                  <span className="asha-categories__btn">View Items</span>
                </div>
              </div>
              <h4 className="asha-categories__item-title">{cat.name}</h4>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CafeCategories = ({ config, navigate }) => {
  const { menu } = config;
  if (!menu?.categories?.length) return null;

  return (
    <section id="categories" className="cafe-categories">
      <div className="container">
        <div className="cafe-categories__header text-center mb-5">
          <span className="cafe-categories__kicker">{menu.sub_heading}</span>
          <h2 className="cafe-categories__title">{menu.heading || 'Brew Categories'}</h2>
          <div className="cafe-menu__ornament">
            <span className="cafe-ornament-line" />
            <span className="cafe-ornament-icon">☕</span>
            <span className="cafe-ornament-line" />
          </div>
          <p className="cafe-categories__desc">{menu.description}</p>
        </div>

        <div className="cafe-categories__grid">
          {menu.categories.map((cat, idx) => (
            <div
              key={cat.id || idx}
              className="cafe-categories__item"
              onClick={() => { if (cat.id) sessionStorage.setItem('pending_category_id', String(cat.id)); navigate('/restaurants'); }}
            >
              <div className="cafe-categories__img-box">
                <img src={cat.image} alt={cat.name} />
                <div className="cafe-categories__overlay">
                  <span className="cafe-categories__btn">Explore Collection <i className="fa fa-arrow-right" /></span>
                </div>
              </div>
              <div className="cafe-categories__info">
                <h4 className="cafe-categories__item-title">{cat.name}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DryFruitsCategories = ({ config, navigate }) => {
  const { menu } = config;
  if (!menu?.categories?.length) return null;

  return (
    <section id="categories" className="df-categories py-5">
      <div className="container">
        <div className="df-categories__header text-center mb-5">
          <h2 className="df-categories__title">{menu.heading || 'DRY FRUITS AND NUTS'}</h2>
        </div>
        <div className="df-categories__grid">
          {menu.categories.map((cat, idx) => (
            <div
              key={cat.id || idx}
              className="df-categories__item"
              onClick={() => { if (cat.id) sessionStorage.setItem('pending_category_id', String(cat.id)); navigate('/restaurants'); }}
            >
              <div className="df-categories__img-box">
                <img src={cat.image} alt={cat.name} />
              </div>
              <h4 className="df-categories__name">{cat.name}</h4>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE 16 — ARISA COLLECTION — "Shop by Category"
// Clean rounded cards, image with bottom gradient caption, subtle zoom.
// ═══════════════════════════════════════════════════════════════════
const SweetStoreCategories = ({ config, navigate, nearestStore }) => {
  const { menu } = config;
  const [fetchedCategories, setFetchedCategories] = useState(null);
  const [firstItemImageByCategory, setFirstItemImageByCategory] = useState({});
  const branchId = nearestStore?.id;

  // config.menu.categories is never populated anywhere in the app (same as
  // every other template's CategoryGrid variant) — the shop page's real
  // category list only lives behind /web/item-category. Fetch it directly,
  // the same way MenuSection already fetches its own category tabs, and
  // share the same cross-page cache the shop page uses.
  useEffect(() => {
    let cancelled = false;

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
    const X_API_KEY = process.env.REACT_APP_X_API_KEY;
    const X_TENANT = getTenantId();
    const commonHeaders = {
      'Content-Type': 'application/json',
      'X-Api-Key': X_API_KEY,
      'X-Tenant': X_TENANT
    };
    const categoriesUrl = branchId
      ? `${API_BASE_URL}/web/item-category?branch_id=${branchId}`
      : `${API_BASE_URL}/web/item-category`;
    const itemsUrl = branchId
      ? `${API_BASE_URL}/web/item?branch_id=${branchId}`
      : `${API_BASE_URL}/web/item`;

    const cachedCategories = apiCache.getCategories();

    const categoriesPromise = cachedCategories
      ? Promise.resolve(cachedCategories)
      : fetch(categoriesUrl, { headers: commonHeaders })
        .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch categories')))
        .then(data => {
          const categories = data.data || [];
          apiCache.setCategories(categories);
          return categories;
        })
        .catch(() => []);

    // Real per-category images are frequently unset by merchants, so fall
    // back to the first real product photo already available for that
    // category — reusing existing item data rather than inventing an image.
    const itemsPromise = fetch(itemsUrl, { headers: commonHeaders })
      .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch items')))
      .then(data => data.data || [])
      .catch(() => []);

    Promise.all([categoriesPromise, itemsPromise]).then(([categories, items]) => {
      if (cancelled) return;
      // Hide categories with no item for this branch (the category list is
      // tenant-wide; the item list follows the branch's outlet menu / master
      // menu). Skipped when no items came back so a failed fetch can't blank it.
      const branchCategoryIds = new Set(items.map(item => String(item.item_category_id)));
      setFetchedCategories(
        items.length > 0
          ? categories.filter(cat => String(cat.id) === '0' || branchCategoryIds.has(String(cat.id)))
          : categories
      );

      const imageByCategory = {};
      items.forEach(item => {
        const catId = String(item.item_category_id);
        if (!imageByCategory[catId]) {
          const itemImage = item.thumb || item.cover || item.image;
          if (itemImage) imageByCategory[catId] = itemImage;
        }
      });
      setFirstItemImageByCategory(imageByCategory);
    });

    return () => { cancelled = true; };
  }, [branchId]);

  const categories = (fetchedCategories || menu?.categories || [])
    .filter(cat => cat.name && !cat.name.toLowerCase().includes('all'))
    .slice(0, 8)
    .map(cat => ({
      ...cat,
      image: cat.image || firstItemImageByCategory[String(cat.id)]
    }));

  if (!categories.length) return null;

  return (
    <section id="categories" className="t16-categories">
      <div className="t16-categories__container">
        <div className="t16-categories__header">
          <h2 className="t16-categories__title">{menu?.heading || 'Our Categories'}</h2>
          <span className="t16-categories__underline" aria-hidden="true" />
        </div>

        <div className="t16-categories__row">
          {categories.map((cat, idx) => (
            <button
              key={cat.id || idx}
              type="button"
              className="t16-categories__item"
              onClick={() => { if (cat.id) sessionStorage.setItem('pending_category_id', String(cat.id)); navigate('/restaurants'); }}
            >
              <span className="t16-categories__ring">
                <FallbackImg src={cat.image} alt={cat.name} />
              </span>
              <span className="t16-categories__name">{cat.name}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const CategoryGrid = ({ config, nearestStore }) => {
  const navigate = useNavigate();
  const type = (config.templateType || 'template1').toLowerCase().replace(/-/g, '_');

  switch (type) {
    case 'template14':
    case 'cafe_premium':
      return <CafeCategories config={config} navigate={navigate} />;
    case 'template15':
    case 'dry_fruits':
      return <DryFruitsCategories config={config} navigate={navigate} />;
    case 'template16':
    case 'arisa_collection':
      return <SweetStoreCategories config={config} navigate={navigate} nearestStore={nearestStore} />;
    default:
      return <AshaCategories config={config} navigate={navigate} />;
  }
};

export default CategoryGrid;
